<template>
    <div class="toolbar-panels" :class="{ 'toolbar-panels-single': visiblePanelsCount === 1 }">
        <div v-if="showCreatePanel" class="toolbar-panel toolbar-panel--create">
            <div class="create-box">
                <div class="toolbar-panel-title mb-0">Créer une table</div>

                <div class="create-box__section">
                    <label class="form-label small text-muted mb-1 d-block">Nom de la table</label>
                    <input
                        :value="createName"
                        type="text"
                        class="form-control create-box__input"
                        placeholder="Ex. Terrasse 12"
                        @input="updateCreateName" />
                </div>

                <div class="create-box__section">
                    <label class="form-label small text-muted mb-1 d-block">Capacité maximale</label>
                    <div class="seats-stepper">
                        <button
                            type="button"
                            class="seats-stepper__button"
                            :disabled="createSeats <= 1 || isCreating"
                            aria-label="Retirer un couvert"
                            @click="decrementSeats">
                            -
                        </button>
                        <input
                            :value="createSeats"
                            type="number"
                            min="1"
                            max="100"
                            class="form-control seats-stepper__input"
                            @input="updateCreateSeats" />
                        <button
                            type="button"
                            class="seats-stepper__button"
                            :disabled="createSeats >= 100 || isCreating"
                            aria-label="Ajouter un couvert"
                            @click="incrementSeats">
                            +
                        </button>
                    </div>
                </div>

                <div class="create-box__section">
                    <label class="form-label small text-muted mb-2 d-block">Utilisation de la table</label>
                    <div class="usage-options">
                        <label v-for="option in usageOptions" :key="option.value" class="form-label usage-option">
                            <input
                                :checked="createUsageMode === option.value"
                                type="radio"
                                name="create-table-usage-mode"
                                class="usage-option__radio"
                                @change="updateCreateUsageMode(option.value)" />
                            <span class="usage-option__content">
                                <span class="usage-option__title">{{ option.label }}</span>
                                <span class="usage-option__description">{{ option.description }}</span>
                            </span>
                        </label>
                    </div>
                </div>

                <div v-if="createUsageMode === 'custom'" class="create-box__section">
                    <label class="form-label small text-muted mb-2 d-block">Si personnalisée :</label>
                    <div class="custom-pax-grid">
                        <button
                            v-for="pax in availablePaxChoices"
                            :key="pax"
                            type="button"
                            class="custom-pax-chip"
                            :class="{ 'custom-pax-chip--active': createCustomEnabledPax.includes(pax) }"
                            @click="toggleCustomPax(pax)">
                            {{ pax }}
                        </button>
                    </div>
                </div>

                <div class="create-box__actions">
                    <button
                        type="button"
                        class="btn btn-success btn-sm btn-circle create-box__submit"
                        :disabled="isCreating"
                        @click="$emit('create-table')">
                        <i class="fa fa-plus me-1"></i>Ajouter
                    </button>
                </div>
            </div>
        </div>

        <div v-if="showFilterPanel" :class="['toolbar-panel', { 'toolbar-panel--compact': compactFilterPanel }]">
            <div class="toolbar-panel-title">Plan de salle</div>
            <label class="form-label small text-muted mb-1">Filtrer par plan de salle</label>
            <select :value="selectedSeatingPlanId" class="form-select select-native mb-0" @change="updateSelectedSeatingPlan">
                <option value="">Tous les plans</option>
                <option v-for="plan in seatingPlans" :key="plan.id" :value="String(plan.id)">
                    {{ plan.name }}
                </option>
            </select>
            <div class="filter-count mt-3">
                {{ displayedTablesCount }} table{{ displayedTablesCount > 1 ? "s" : "" }} affichée{{ displayedTablesCount > 1 ? "s" : "" }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "OrderingTableTablesToolbar",
    props: {
        createName: {
            type: String,
            required: true,
        },
        createSeats: {
            type: Number,
            required: true,
        },
        createUsageMode: {
            type: String,
            required: true,
        },
        createCustomEnabledPax: {
            type: Array,
            required: true,
        },
        isCreating: {
            type: Boolean,
            required: true,
        },
        seatingPlans: {
            type: Array,
            required: true,
        },
        selectedSeatingPlanId: {
            type: String,
            required: true,
        },
        displayedTablesCount: {
            type: Number,
            required: true,
        },
        showCreatePanel: {
            type: Boolean,
            default: true,
        },
        showFilterPanel: {
            type: Boolean,
            default: true,
        },
        compactFilterPanel: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        visiblePanelsCount() {
            return [this.showCreatePanel, this.showFilterPanel].filter(Boolean).length;
        },
        usageOptions() {
            const seats = this.sanitizedSeats;
            const standardStart = seats <= 1 ? 1 : 2;

            return [
                {
                    value: "flexible",
                    label: "Flexible",
                    description: `accepte les groupes de 1 à ${seats} personne${seats > 1 ? "s" : ""}`,
                },
                {
                    value: "standard",
                    label: "Standard",
                    description: `accepte les groupes de ${standardStart} à ${seats} personne${seats > 1 ? "s" : ""}`,
                },
                {
                    value: "strict",
                    label: "Stricte",
                    description: `uniquement ${seats} personne${seats > 1 ? "s" : ""}`,
                },
                {
                    value: "custom",
                    label: "Personnalisée",
                    description: "définissez précisément les capacités autorisées",
                },
            ];
        },
        sanitizedSeats() {
            return Math.min(100, Math.max(1, Number.parseInt(this.createSeats, 10) || 1));
        },
        availablePaxChoices() {
            return Array.from({ length: this.sanitizedSeats }, (_, index) => index + 1);
        },
    },
    methods: {
        updateCreateName(event) {
            this.$emit("update:create-name", event.target.value.trimStart());
        },
        updateCreateSeats(event) {
            const value = Number.parseInt(event.target.value, 10);
            this.syncSeatsState(Number.isNaN(value) ? 0 : value);
        },
        decrementSeats() {
            this.syncSeatsState(Math.max(1, this.sanitizedSeats - 1));
        },
        incrementSeats() {
            this.syncSeatsState(Math.min(100, this.sanitizedSeats + 1));
        },
        syncSeatsState(nextSeats) {
            const seats = Math.min(100, Math.max(1, Number.parseInt(nextSeats, 10) || 1));
            this.$emit("update:create-seats", seats);

            const normalizedCustom = this.createCustomEnabledPax
                .map((value) => Number.parseInt(value, 10))
                .filter((value) => Number.isInteger(value) && value >= 1 && value <= seats)
                .sort((left, right) => left - right);

            if (normalizedCustom.length === 0) {
                this.$emit("update:create-custom-enabled-pax", this.availableRange(1, seats));
                return;
            }

            this.$emit("update:create-custom-enabled-pax", normalizedCustom);
        },
        updateCreateUsageMode(value) {
            this.$emit("update:create-usage-mode", value);

            if (value !== "custom") {
                return;
            }

            const normalizedCustom = this.createCustomEnabledPax
                .map((pax) => Number.parseInt(pax, 10))
                .filter((pax) => Number.isInteger(pax) && pax >= 1 && pax <= this.sanitizedSeats)
                .sort((left, right) => left - right);

            this.$emit("update:create-custom-enabled-pax", normalizedCustom.length ? normalizedCustom : this.availableRange(1, this.sanitizedSeats));
        },
        toggleCustomPax(value) {
            if (this.isCreating) {
                return;
            }

            const current = this.createCustomEnabledPax.includes(value)
                ? this.createCustomEnabledPax.filter((pax) => pax !== value)
                : [...this.createCustomEnabledPax, value];

            const normalized = current
                .map((pax) => Number.parseInt(pax, 10))
                .filter((pax) => Number.isInteger(pax) && pax >= 1 && pax <= this.sanitizedSeats)
                .sort((left, right) => left - right);

            this.$emit("update:create-custom-enabled-pax", normalized);
        },
        updateSelectedSeatingPlan(event) {
            this.$emit("update:selected-seating-plan-id", event.target.value);
        },
        availableRange(start, end) {
            const safeStart = Math.max(1, start);
            const safeEnd = Math.max(safeStart, end);

            return Array.from({ length: safeEnd - safeStart + 1 }, (_, index) => safeStart + index);
        },
    },
};
</script>

<style scoped lang="scss">
.toolbar-panels {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.toolbar-panels-single {
    grid-template-columns: minmax(0, 1fr);
}

.toolbar-panel {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.toolbar-panel--compact {
    max-width: 360px;
}

.toolbar-panel--create {
    display: flex;
    align-items: flex-start;
}

.toolbar-panel-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 12px;
}

.create-box {
    width: min(100%, 420px);
    border: 1px solid #dbe3ea;
    border-radius: 14px;
    padding: 18px;
    background: #ffffff;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.create-box__intro {
    font-size: 13px;
    line-height: 1.5;
    color: #6b7280;
}

.create-box__section {
    margin-top: 18px;
}

.create-box__input {
    max-width: 300px;
}

.seats-stepper {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    border: 1px solid #d9e1e7;
    border-radius: 12px;
    background: #f9fafb;
}

.seats-stepper__button {
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 10px;
    background: #ffffff;
    color: #083e60;
    font-size: 18px;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.seats-stepper__button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
}

.seats-stepper__input {
    width: 72px;
    text-align: center;
    border: 0;
    background: transparent;
    box-shadow: none;
    padding-left: 6px;
    padding-right: 6px;
    font-weight: 600;
    color: #111827;
}

.seats-stepper__input:focus {
    box-shadow: none;
}

.create-box__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 22px;
}

.create-box__submit {
    min-width: 112px;
}

.usage-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.usage-option {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0;
    cursor: pointer;
}

.usage-option__radio {
    margin-top: 3px;
}

.usage-option__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.usage-option__title {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
}

.usage-option__description {
    font-size: 12px;
    color: #4b5563;
}

.custom-pax-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.custom-pax-chip {
    min-width: 40px;
    height: 34px;
    border: 1px solid #d9e1e7;
    border-radius: 10px;
    background: #ffffff;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
}

.custom-pax-chip--active {
    border-color: #083e60;
    background: rgba(8, 62, 96, 0.08);
    color: #083e60;
}

.filter-count {
    height: 38px;
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #f3f4f6;
    color: #374151;
    font-size: 13px;
    font-weight: 600;
}

@media (max-width: 991px) {
    .toolbar-panels {
        grid-template-columns: 1fr;
    }

    .toolbar-panel--compact {
        max-width: none;
    }

    .create-box {
        width: 100%;
    }

    .create-box__input {
        max-width: none;
    }
}
</style>
